import { useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { authStore } from '../store/authStore';
import { useAuthState } from './useAuthState';
import {
  fetchCourseConfigStart,
  fetchCourseConfigSuccess,
  fetchCourseConfigFailure
} from '../store/appDataStore';
import {
  fetchGroupSessionsStart,
  fetchGroupSessionsSuccess,
  fetchGroupSessionsFailure
} from '../store/appDataStore';
import { 
  setUserInfos,
  selectUserInfos
} from '../store/userDataStore';
import { authService } from '../services/authService';
import { userService } from '../services/userService';
import { fetchCourseStatsStart, fetchCourseStatsSuccess, fetchCourseStatsFailure } from '../store/appDataStore';
import { statsService } from '../services/statsService';

export const useAuth = () => {
  const dispatch = useDispatch();
  const authState = useAuthState();
  const userInfos = useSelector((state: RootState) => selectUserInfos(state));
  const navigate = useNavigate();

  // Factorisation des actions 1 à 6 (login ou MFA)
  // Correction typage : conversion du format us_e2e_tests si besoin
  const handlePostLoginActions = useCallback(async (user_id: number, access_token: string) => {
    // ACTION 2 : Récupérer les infos détaillées de l'utilisateur
    try {
      const userInfo = await userService.getUserInfos(user_id, access_token);
      if (userInfo) {
        dispatch(setUserInfos(userInfo));
      }
    } catch (e) {
      console.error('Erreur récupération infos utilisateur:', e);
    }
    // ACTION 3 : Récupérer les paramètres de configuration du cours
    try {
      dispatch(fetchCourseConfigStart());
      const courseConfig = await userService.getCourseConfig(access_token);
      if (courseConfig) {
        dispatch(fetchCourseConfigSuccess(courseConfig));
      } else {
        dispatch(fetchCourseConfigFailure('Aucune configuration de cours trouvée'));
      }
    } catch (e) {
      dispatch(fetchCourseConfigFailure('Erreur récupération configuration du cours'));
      console.error('Erreur récupération paramètres de configuration du cours:', e);
    }
    // (Plus d'ACTION 4 : les résultats CI ne sont plus tous téléchargés à la connexion — chaque page
    // demande au serveur les données dont elle a besoin : courbes Charts, page de résultats d'un cycle.)
    // ACTION 5 : Récupérer les sessions de groupe
    try {
      dispatch(fetchGroupSessionsStart());
      const groupSessionsResponse = await fetch(`${import.meta.env.VITE_REACT_APP_API_EP_URI}/group-sessions`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${access_token}`
        },
      });
      if (!groupSessionsResponse.ok) {
        throw new Error(`HTTP error! status: ${groupSessionsResponse.status}`);
      }
      const data = await groupSessionsResponse.json();
      dispatch(fetchGroupSessionsSuccess(data.group_sessions));
    } catch (e) {
      dispatch(fetchGroupSessionsFailure('Erreur récupération sessions de groupe'));
      console.error('Erreur récupération sessions de groupe:', e);
    }
    // ACTION 6 : Récupérer les stats du cours
    try {
      dispatch(fetchCourseStatsStart());
      const courseStats = await statsService.getCourseStats(access_token);
      if (courseStats) {
        dispatch(fetchCourseStatsSuccess(courseStats));
      } else {
        dispatch(fetchCourseStatsFailure('Aucune statistique trouvée'));
      }
    } catch (e) {
      dispatch(fetchCourseStatsFailure('Erreur récupération statistiques du cours'));
      console.error('Erreur récupération statistiques du cours:', e);
    }
  }, [dispatch]);

  // Fonction de login
  const login = useCallback(async (username: string, password: string) => {
    authStore.loginStart();
    try {
      const response = await authService.loginUser(username, password);

      // Cas MFA requis : on ne déclare pas loginSuccess, on retourne les données MFA
      if (response.status === 'mfa_required') {
        authStore.setMfaRequired();
        return {
          mfaRequired: true,
          sessionData: response.session_data,
          message: response.message
        };
      }

      if (response.status === 'ok') {
        // ACTION 1 : Mettre à jour le store avec les informations de l'utilisateur et les tokens
        authStore.loginSuccess({
          user: {
            id: response.user_id!,
            login: response.login!
          },
          token: response.access_token!,
          refreshToken: response.refresh_token!,
          expiresIn: undefined
        });
        await handlePostLoginActions(response.user_id!, response.access_token!);
        return { mfaRequired: false };
      } else {
        authStore.loginFailure(response.message || 'Erreur de connexion');
        return { mfaRequired: false };
      }
    } catch (error) {
      authStore.loginFailure('Une erreur inattendue est survenue');
      return { mfaRequired: false };
    }
  }, [handlePostLoginActions]);

  // Connexion via un ticket CAS SSO réel (callback après redirection, connexion principale
  // prod — voir VITE_AUTH_MODE). Même traitement de succès que login() : pas de duplication.
  const loginWithCasTicket = useCallback(async (ticket: string) => {
    authStore.loginStart();
    try {
      const response = await authService.validateCasTicket(ticket);

      if (response.status === 'ok') {
        authStore.loginSuccess({
          user: {
            id: response.user_id!,
            login: response.login!
          },
          token: response.access_token!,
          refreshToken: response.refresh_token!,
          expiresIn: undefined
        });
        await handlePostLoginActions(response.user_id!, response.access_token!);
        return { success: true };
      } else {
        authStore.loginFailure(response.message || 'Erreur de connexion CAS');
        return { success: false, error: response.message };
      }
    } catch (error: any) {
      authStore.loginFailure(error?.message || 'Une erreur inattendue est survenue');
      return { success: false, error: error?.message };
    }
  }, [handlePostLoginActions]);

  // Fonction pour soumettre le code MFA
  const submitMfaCode = useCallback(async (username: string, password: string, mfaToken: string, sessionData: any) => {
    authStore.loginMfaStart();
    try {
      const response = await authService.submitMfaCode(username, password, mfaToken, sessionData);

      if (response.status === 'ok') {
        // Même traitement que login réussi
        authStore.loginSuccess({
          user: {
            id: response.user_id!,
            login: response.login!
          },
          token: response.access_token!,
          refreshToken: response.refresh_token!,
          expiresIn: undefined
        });
        await handlePostLoginActions(response.user_id!, response.access_token!);
        return { success: true };
      } else {
        authStore.loginFailure(response.message || 'Code MFA incorrect');
        return { success: false, error: response.message };
      }
    } catch (error) {
      authStore.loginFailure('Erreur lors de la vérification du code MFA');
      return { success: false, error: 'Une erreur inattendue est survenue' };
    }
  }, [handlePostLoginActions]);

  // Pour reset l'état lors de l'annulation MFA
  const resetAuthState = useCallback(() => {
    authStore.clearError();
  }, []);

  // Fonction de logout
  const handleLogout = useCallback(async () => {
    const accessToken = authState.token;
    const refreshToken = authState.refreshToken;
    let accessError = null;
    let refreshError = null;

    // Révoquer le refresh token
    if (refreshToken) {
      try {
        await authService.logoutRefreshToken(refreshToken);
      } catch (error) {
        refreshError = error;
        console.error('Logout refresh token error:', error);
      }
    }
    console.log('Refresh logout');
    // Révoquer l'access token
    if (accessToken) {
      try {
        await authService.logoutAccessToken(accessToken);
      } catch (error) {
        accessError = error;
        console.error('Logout access token error:', error);
      }
    }
    console.log('Access logout');

    // Rediriger vers la page d'accueil avant de mettre à jour le store
    navigate('/');
    // On met à jour le store côté front même si une des requêtes échoue
    authStore.logout();
  }, [authState.token, authState.refreshToken, navigate]);

  // Auto-refresh du token
  useEffect(() => {
    if (!authState.isAuthenticated || !authState.tokenExpiresAt) {
      return;
    }

    const checkTokenExpiration = () => {
      if (authService.isTokenExpiringSoon()) {
        authService.handleTokenRefresh();
      }
    };

    // Vérifier toutes les minutes
    const interval = setInterval(checkTokenExpiration, 60000);

    // Vérification immédiate
    checkTokenExpiration();

    return () => clearInterval(interval);
  }, [authState.isAuthenticated, authState.tokenExpiresAt]);

  // Validation de la session restaurée depuis localStorage (au montage).
  // authStore persiste token/isAuthenticated, mais userData (Redux, non persisté) repart
  // vide à chaque F5 — sans ce ré-appel, la session "survit" mais affiche un utilisateur
  // vide (userInfos jamais repeuplé en dehors du flux login()). On revalide le token
  // (succès → frais ; échec → le store déconnecte via refreshTokenFailure, retour au
  // login) puis on relance les mêmes actions post-login qu'un login classique.
  const startupValidated = useRef(false);
  useEffect(() => {
    if (startupValidated.current) return;
    startupValidated.current = true;

    const { isAuthenticated, refreshToken, user } = authStore.getState();
    if (!isAuthenticated || !refreshToken || !user) return;

    (async () => {
      const freshToken = await authService.handleTokenRefresh();
      if (freshToken) {
        await handlePostLoginActions(user.id, freshToken);
      }
    })();
  }, [handlePostLoginActions]);

  // Récupérer un token valide
  const getValidToken = useCallback(async () => {
    return await authService.getValidToken();
  }, []);

  // URL de redirection vers le vrai CAS (connexion principale — VITE_AUTH_MODE=cas-only)
  const getCasLoginUrl = useCallback(async () => {
    return await authService.getCasLoginUrl();
  }, []);

  return {
  ...authState,
  user: userInfos,
  login,
  loginWithCasTicket,
  getCasLoginUrl,
  submitMfaCode,
  logout: handleLogout,
  getValidToken,
  resetAuthState,
  isTokenExpiringSoon: authService.isTokenExpiringSoon()
  };
};